import { useCallback, useEffect, useRef, useState } from "react";
import {
  extractSignals,
  type ExtractedSignals,
  type Familiarity,
  type Readiness,
} from "@itspip/shared";
import { getAccessToken } from "@/lib/auth";

const apiUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

/** Cap free-text fields client-side (backend also enforces). */
export const MAX_COACH_INPUT_CHARS = 2000;

export type CoachRole = "user" | "assistant";

export interface CoachMessage {
  role: CoachRole;
  content: string;
}

export type CoachError =
  | { kind: "unavailable"; message: string }
  | { kind: "rate_limited"; message: string }
  | { kind: "network"; message: string }
  | { kind: "aborted"; message: string };

export type ObjectiveLevel = "department" | "team" | "individual";
export type ObjectiveType = "committed" | "aspirational";
export type KrTypeInput = "percentage" | "binary" | "metric";
export type KrMode = "individual" | "holistic";

export type { Readiness, Familiarity } from "@itspip/shared";

export interface AnalyzeObjectivePayload {
  objective_statement: string;
  level: ObjectiveLevel;
  type: ObjectiveType;
  prior_feedback?: readonly CoachMessage[];
  /** Optional per-session familiarity override. Server falls back to user record. */
  familiarity?: Familiarity;
  focus_area_id?: string | null;
  tactic_id?: string | null;
}

export interface CoachKrDraft {
  title: string;
  type: KrTypeInput;
  target_value?: number | null;
}

export interface AnalyzeKrsPayload {
  objective_statement: string;
  key_results: readonly CoachKrDraft[];
  mode: KrMode;
  prior_feedback?: readonly CoachMessage[];
  /** UUID of the saved parent Objective; server injects into Block 2. */
  parent_objective_id?: string;
  familiarity?: Familiarity;
}

export interface CoachSignals {
  readiness: Readiness | null;
  returnToObjective: boolean;
  modeRecalibrated: Familiarity | null;
}

interface CoachStreamResult {
  /** Visible feedback. While streaming, contains raw stream (may include
   *  trailing sentinels as they arrive). After stream end, sentinels are
   *  stripped and only the visible body remains. */
  feedback: string;
  isStreaming: boolean;
  error: CoachError | null;
  signals: CoachSignals;
  reset: () => void;
}

interface UseCoachObjectiveResult extends CoachStreamResult {
  analyze: (input: AnalyzeObjectivePayload) => Promise<void>;
}

interface UseCoachKeyResultsResult extends CoachStreamResult {
  analyze: (input: AnalyzeKrsPayload) => Promise<void>;
}

const ERROR_MESSAGES: Record<string, string> = {
  AI_SERVICE_UNAVAILABLE:
    "Coaching is temporarily unavailable. You can keep writing and try again in a few minutes.",
  AI_RATE_LIMITED:
    "You've used coaching frequently. Please wait a few minutes before trying again.",
  AI_TIMEOUT: "The coach took too long to respond. Please try again.",
  AI_MALFORMED_RESPONSE: "The coach returned an unexpected response. Please try again.",
  UNAUTHENTICATED: "Your session has expired. Please sign in again.",
  VALIDATION_ERROR: "Your draft couldn't be analyzed. Check the length and try again.",
};

const NETWORK_ERROR_MESSAGE =
  "Network problem while streaming coaching feedback. Your draft is preserved — try again.";

const EMPTY_SIGNALS: CoachSignals = {
  readiness: null,
  returnToObjective: false,
  modeRecalibrated: null,
};

export function useCoachObjective(): UseCoachObjectiveResult {
  return useCoachStream<AnalyzeObjectivePayload>("/v1/coach/analyze-objective");
}

export function useCoachKeyResults(): UseCoachKeyResultsResult {
  return useCoachStream<AnalyzeKrsPayload>("/v1/coach/analyze-key-results");
}

function useCoachStream<TInput extends object>(path: string): {
  feedback: string;
  isStreaming: boolean;
  error: CoachError | null;
  signals: CoachSignals;
  reset: () => void;
  analyze: (input: TInput) => Promise<void>;
} {
  const [feedback, setFeedback] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<CoachError | null>(null);
  const [signals, setSignals] = useState<CoachSignals>(EMPTY_SIGNALS);
  const abortRef = useRef<AbortController | null>(null);
  const rawRef = useRef("");

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    rawRef.current = "";
    setFeedback("");
    setError(null);
    setIsStreaming(false);
    setSignals(EMPTY_SIGNALS);
  }, []);

  const analyze = useCallback(
    async (input: TInput) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      rawRef.current = "";
      setFeedback("");
      setError(null);
      setSignals(EMPTY_SIGNALS);
      setIsStreaming(true);

      try {
        const token = await getAccessToken();
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
        };
        if (token) headers.Authorization = `Bearer ${token}`;

        const response = await fetch(`${apiUrl}${path}`, {
          method: "POST",
          headers,
          body: JSON.stringify(input),
          signal: controller.signal,
        });

        if (!response.ok) {
          setError(await mapHttpError(response));
          setIsStreaming(false);
          return;
        }

        const body = response.body;
        if (!body) {
          setError({ kind: "unavailable", message: ERROR_MESSAGES.AI_MALFORMED_RESPONSE! });
          setIsStreaming(false);
          return;
        }

        await consumeSse(body, controller.signal, (delta) => {
          rawRef.current += delta;
          setFeedback((prev) => prev + delta);
        });

        const parsed: ExtractedSignals = extractSignals(rawRef.current);
        setFeedback(parsed.visible);
        setSignals({
          readiness: parsed.readiness ?? null,
          returnToObjective: parsed.returnToObjective === true,
          modeRecalibrated: parsed.modeRecalibrated ?? null,
        });
        setIsStreaming(false);
      } catch (e) {
        if (controller.signal.aborted) {
          setError({ kind: "aborted", message: "Coaching was cancelled." });
        } else {
          setError({ kind: "network", message: NETWORK_ERROR_MESSAGE });
        }
        setIsStreaming(false);
        // Intentionally do not log `e` or the payload — may contain user content.
      }
    },
    [path],
  );

  return { feedback, isStreaming, error, signals, reset, analyze };
}

async function mapHttpError(response: Response): Promise<CoachError> {
  let code: string | undefined;
  try {
    const body = (await response.json()) as { error?: { code?: string } };
    code = body.error?.code;
  } catch {
    // Non-JSON body; fall through to status-based mapping.
  }
  if (code === "AI_RATE_LIMITED" || response.status === 429) {
    return { kind: "rate_limited", message: ERROR_MESSAGES.AI_RATE_LIMITED! };
  }
  if (code && ERROR_MESSAGES[code]) {
    return { kind: "unavailable", message: ERROR_MESSAGES[code]! };
  }
  return { kind: "unavailable", message: ERROR_MESSAGES.AI_SERVICE_UNAVAILABLE! };
}

/**
 * Consume Anthropic-style SSE. Each frame is a pair of lines:
 *   event: <name>
 *   data: <json>
 * separated by a blank line. We only extract text from content_block_delta.
 */
async function consumeSse(
  body: ReadableStream<Uint8Array>,
  signal: AbortSignal,
  onDelta: (text: string) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (!signal.aborted) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let sep = buffer.indexOf("\n\n");
      while (sep !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const delta = parseFrame(frame);
        if (delta) onDelta(delta);
        sep = buffer.indexOf("\n\n");
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* noop */
    }
  }
}

function parseFrame(frame: string): string | null {
  const lines = frame.split("\n");
  let dataLine: string | null = null;
  for (const line of lines) {
    if (line.startsWith("data:")) {
      dataLine = line.slice(5).trim();
      break;
    }
  }
  if (!dataLine || dataLine === "[DONE]") return null;
  try {
    const payload = JSON.parse(dataLine) as {
      type?: string;
      delta?: { type?: string; text?: string };
    };
    if (payload.type === "content_block_delta" && payload.delta?.type === "text_delta") {
      return payload.delta.text ?? "";
    }
  } catch {
    // Ignore malformed frames — stream may contain keep-alive noise.
  }
  return null;
}
